"use client";

import { useState } from "react";
import { ArrowLeft, Droplets, Wallet } from "lucide-react";
import { FriendbotFund } from "@/components/dashboard/FriendbotFund";
import { WalletFund } from "@/components/dashboard/WalletFund";
import { cn } from "@/lib/utils";

type FundView = "default" | "friendbot" | "walletfund";

interface FundAccountCardProps {
  smartAccountAddress: string;
  onSuccess: () => void;
}

export function FundAccountCard({ smartAccountAddress, onSuccess }: FundAccountCardProps) {
  const [view, setView] = useState<FundView>("default");

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--border-default)" }}
      aria-label="Fund your account"
    >
      {/* Card header */}
      <div
        className="flex items-center gap-3 px-5 py-4"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        {/* Back button — shown in sub-views */}
        {view !== "default" && (
          <button
            type="button"
            onClick={() => { setView("default"); }}
            className={cn(
              "flex items-center justify-center w-6 h-6 rounded transition-colors",
              "hover:bg-[var(--surface-hi)]",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]",
            )}
            aria-label="Back to fund options"
          >
            <ArrowLeft size={14} style={{ color: "var(--text-secondary)" }} aria-hidden="true" />
          </button>
        )}

        <h2
          className="font-serif font-normal flex-1"
          style={{ fontSize: "1rem", color: "var(--text-primary)" }}
        >
          {view === "default" && "Fund Account"}
          {view === "friendbot" && "Get Testnet USDC"}
          {view === "walletfund" && "Fund from Wallet"}
        </h2>
      </div>

      {/* Default view: two option buttons */}
      {view === "default" && (
        <div className="px-5 py-4 flex flex-col gap-3">
          <p className="text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Fund your smart account to start earning, trading, and allocating liquidity across Stellar.
          </p>

          {/* Get Testnet USDC */}
          <button
            type="button"
            onClick={() => { setView("friendbot"); }}
            className={cn(
              "w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-medium transition-all",
              "hover:brightness-110 active:scale-[0.98]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]",
            )}
            style={{
              background: "var(--accent)",
              color: "var(--bg)",
            }}
          >
            <Droplets size={15} aria-hidden="true" />
            Get Testnet USDC
          </button>

          {/* Fund from Wallet */}
          <button
            type="button"
            onClick={() => { setView("walletfund"); }}
            className={cn(
              "w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-medium transition-all",
              "hover:brightness-110 active:scale-[0.98]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#60a5fa]",
            )}
            style={{
              background: "#2563eb",
              color: "#fff",
            }}
          >
            <Wallet size={15} aria-hidden="true" />
            Fund from Wallet
          </button>
        </div>
      )}

      {/* Friendbot view */}
      {view === "friendbot" && (
        <div className="px-5 py-4">
          <FriendbotFund onSuccess={onSuccess} />
        </div>
      )}

      {/* WalletFund view */}
      {view === "walletfund" && (
        <div className="px-5 py-4">
          <WalletFund smartAccountAddress={smartAccountAddress} onSuccess={onSuccess} />
        </div>
      )}
    </div>
  );
}
